import { navigate } from "gatsby"
import { useEffect, useMemo, useState } from "react"

import {
  marketingClient,
  orderClient,
  useMutation,
  useQueryClient,
} from "@trueskin-web/apis"
import { formatCurrency, isFloat } from "@trueskin-web/functions"
import i18next, { Routes } from "@trueskin-web/translations"

import useCurrentPresets from "../../../hooks/use-current-presets"

const mapCurrentPresetsToPlans = (presets) => {
  if (!presets.length) {
    return []
  }

  const variants = presets.map((currentPreset) => {
    const isSubscription = currentPreset.occurrence !== "occ_once"
    const locale = currentPreset.summary.locale
    const instalments = currentPreset.summary.instalments.count
    const { total, totalWithoutDiscounts, currency } =
      currentPreset.summary.values.perInstalment

    const title = isSubscription
      ? i18next.t("TwoMonthsPresets.title", {
          count: instalments,
        })
      : currentPreset.details.name

    const priceTitle = formatCurrency(
      total / 100,
      currency,
      isFloat(total / 100) ? 2 : 0
    )

    const priceWithoutDiscounts = formatCurrency(
      totalWithoutDiscounts / 100,
      currency
    )

    const benefits = i18next
      .t(`TwoMonthsPresets.benefits.${instalments}`)
      .map((lines) =>
        i18next.t(lines, {
          total: currentPreset.summary.values.total / 100,
        })
      )

    return {
      isSubscription,
      title,
      priceTitle,
      priceWithoutDiscounts,
      locale,
      instalments,
      subscriptionBenefits: isSubscription ? benefits : [],
      id: currentPreset.id,
      isSelected: currentPreset.isSelected,
      isRecommended: currentPreset.isRecommended,
      description: currentPreset.details.description,
      image: currentPreset.details.image,
      details: currentPreset.details.plan,
    }
  })

  return variants.reduce(
    (accum, current) => {
      if (current.isSubscription) {
        accum[0].variants.push(current)
        return accum
      }

      return [...accum, { group: false, variants: [current] }]
    },
    [{ group: true, variants: [] }]
  )
}

const withPresets = (Component) => (props) => {
  const queryClient = useQueryClient()

  const {
    data: { data: currentPresets = [] } = {},
    status: currentPresetsStatus,
    error: currentPresetsError,
  } = useCurrentPresets()

  const {
    mutateAsync: setCurrentPreset,
    status: setCurrentPresetStatus,
    error: setCurrentPresetError,
  } = useMutation(orderClient.setCurrentPreset, {
    onSuccess: ({ data }) =>
      (async ({ customer, preset, values }) => {
        const marketingEventData = {
          userId: customer,
          presetId: preset,
          currency: values.currency,
          value: values.total,
        }

        marketingClient.sendEvent({
          name: marketingClient.EVENT_TYPES_ENUM.PRODUCT_SELECTED,
          data: marketingEventData,
        })
        marketingClient.sendEvent({
          name: marketingClient.EVENT_TYPES_ENUM.SELECT_PAYMENT_OPTION,
          data: marketingEventData,
        })

        await Promise.all([
          queryClient.invalidateQueries("currentPresets"),
          queryClient.invalidateQueries("currentOrderSummary"),
        ])
      })(data),
  })

  const [selectedPlanId, setSelectedPlanId] = useState()

  const handleSelect = (planId) => {
    setSelectedPlanId(planId)

    marketingClient.sendEvent({
      name: marketingClient.EVENT_TYPES_ENUM.PRODUCT_CLICKED,
      data: {
        presetId: planId,
      },
    })
  }

  const handleSubmit = async (planId) => {
    await setCurrentPreset(planId)

    // TODO repurchase codebase in the portal. AC-397
    // if (token) {
    //   return navigate(`${Routes.App.Repurchase.Payment.url}/${token}`)
    // }

    navigate(Routes.App.Start.Shipping.url)
  }

  useEffect(() => {
    const preselectedPlan =
      currentPresets.find(({ isSelected }) => isSelected) ||
      currentPresets.find(({ summary }) => summary.instalments.count === 2)

    if (preselectedPlan) {
      setSelectedPlanId(preselectedPlan.id)
    }
  }, [currentPresets])

  const plans = useMemo(
    () => mapCurrentPresetsToPlans(currentPresets),
    [currentPresets]
  )

  return (
    <Component
      plans={plans}
      selectedPlanId={selectedPlanId}
      isLoading={currentPresetsStatus === "loading"}
      isSubmitting={setCurrentPresetStatus === "loading"}
      error={currentPresetsError}
      submitError={setCurrentPresetError}
      onSelect={handleSelect}
      onSubmit={handleSubmit}
      {...props}
    />
  )
}

export default withPresets
